import React from "react";
import { Box, Button, Grid, Stack } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIosOutlinedIcon from "@mui/icons-material/ArrowBackIosOutlined";
import GradientButton from "./gradients/GradientButton";
import UseMobileMode from "../hooks/useMobileMode";
import ForwardIcon from "@mui/icons-material/Forward";
import { useData } from "../providers/DataProvider";

const RotatingForwardIcon = styled(ForwardIcon)(({ theme, drawerOpen }) => ({
  transform: `rotate(${drawerOpen ? "-270deg" : "-90deg"})`,
}));

const StyledArrow = styled(Box)(() => ({
  width: "32px",
  height: "32px",
  display: "flex",
  fontSize: "22px",
  borderRadius: "50%",
  alignItems: "center",
  marginBlock: "-3px",
  justifyContent: "center",
  border: "2px solid #686868",
  "& .MuiSvgIcon-root": {
    fontSize: "inherit",
    lineHeight: 1,
  },
  "&:first-of-type": {
    marginInlineEnd: "8px",
    marginInlineStart: "-4px",
  },
  "&:last-child": {
    marginInlineStart: "8px",
    marginInlineEnd: "-4px",
  },
}));

const BackButton = styled(Box)(({ theme }) => ({
  top: "2px",
  width: "34px",
  height: "34px",
  display: "flex",
  fontSize: "20px",
  borderRadius: "50%",
  alignItems: "center",
  position: "absolute",
  insetInlineStart: "24px",
  justifyContent: "center",
  background: "rgba(178, 178, 178, 0.60)",
  "& .MuiSvgIcon-root": {
    fontSize: "inherit",
    lineHeight: 1,
  },
}));

function CustomizerDrawerHeader({
  drawerOpen,
  selectedTab,
  buttonText,
  selectPrevCategory,
  selectNextCategory,
  setSelectedCategory,
  setSelectedCol,
  setDrawerOpen,
  setSelectedTab,
  setSelectedCollection,
}) {
  const data = useData();
  const theme = useTheme();
  const isMobile = UseMobileMode();

  // Effects.

  // Handlers.

  const handleStartDesign = () => {
    const audioToPlay = drawerOpen ? data.audios.START_DESIGN : data.audios.MORE_PATCHES;
    audioToPlay.play();
    if (!isMobile) {
      setDrawerOpen((state) => !state);
    } else {
      if (!drawerOpen) {
        setDrawerOpen(true);
      } else {
        handleBack(false);
      }
    }
  };

  const handleBack = (playAudio = true) => {
    if (playAudio) {
      data.audios.START_DESIGN.play();
    }
    setDrawerOpen(false);
    setSelectedCategory(null);
    setSelectedTab(null);
    setSelectedCol(null);
    setSelectedCollection(null);
  };

  // Helpers.

  // Render.
  return (
    <Box sx={{ padding: isMobile ? 0 : drawerOpen ? "10px 25px" : "10px 25px 0px" }}>
      <Grid
        container
        sx={{
          [theme.breakpoints.down("laptop")]: {
            left: "0px",
            right: "0px",
            top: drawerOpen ? "-50px" : "-10px",
            position: "absolute",
            zIndex: 50,
          },
        }}
      >
        {!isMobile && (
          <Grid
            item
            mobile={4}
            sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}
          />
        )}
        <Grid item mobile={!isMobile ? 4 : 12} sx={{ textAlign: "center", position: "relative" }}>
          {isMobile && drawerOpen && (
            <BackButton onClick={handleBack}>
              <ArrowBackIosOutlinedIcon />
            </BackButton>
          )}
          <GradientButton
            onClick={handleStartDesign}
            addStyle={{
              [theme.breakpoints.down("laptop")]: {
                minWidth: "148px",
                ...(selectedTab !== null
                  ? {
                      paddingInline: "4px",
                      justifyContent: "space-between",
                    }
                  : {}),
              },
            }}
          >
            {isMobile && selectedTab !== null && (
              <StyledArrow onClick={selectPrevCategory}>
                <RotatingForwardIcon drawerOpen={drawerOpen} />
              </StyledArrow>
            )}
            <Stack direction="row" gap={1}>
              {buttonText}
              <ForwardIcon sx={{ rotate: drawerOpen ? "-270deg" : "-90deg" }} />
            </Stack>
            {isMobile && selectedTab !== null && (
              <StyledArrow onClick={selectNextCategory}>
                <ArrowForwardIcon />
              </StyledArrow>
            )}
          </GradientButton>
        </Grid>
        {!isMobile && (
          <Grid
            item
            mobile={4}
            sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}
          >
            {drawerOpen && (
              <Button variant="outlined" onClick={() => setDrawerOpen((state) => !state)}>
                Close
              </Button>
            )}
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

export default React.memo(CustomizerDrawerHeader);
